/**
 * WeekDesktopView
 *
 * 7-column magazine-style editorial grid for the desktop weekly planner.
 * Uses CSS Grid (web-only) with masthead and day columns.
 *
 * Implements the same props interface as WeekEditorialView
 * so TheWeeklyScreen can swap views without state logic changes.
 */

import React, { useMemo, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  LayoutChangeEvent,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useRouter } from 'expo-router';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import { PlannedMeal, MealType, MAX_MEALS_PER_SLOT } from '../../redux/mealPlanSlice';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { generateWeekSlots } from '../../services/weekGeneratorService';
import { isEntryMeal } from '../../redux/mealEntry';
import { useDragDrop } from '../../context/DragDropContext';
import { useWebDropZone } from '../../hooks/useWebDragDrop';
import { DesktopMasthead } from './DesktopMasthead';
import { DesktopDayHeader } from './DesktopDayHeader';
import { DesktopMealCard } from './DesktopMealCard';
import { WeekProgressStrip } from './WeekProgressStrip';
import { useToday } from '../../hooks/useToday';

// Drop zone wrapper
interface ColumnDropZoneProps {
  date: string;
  mealType: MealType;
  children: React.ReactNode;
  onMoveMeal?: (mealId: string, newDate: string, newMealType: MealType) => void;
}

const ColumnDropZone: React.FC<ColumnDropZoneProps> = ({ date, mealType, children, onMoveMeal }) => {
  const { state: dragState, registerDropZone, unregisterDropZone } = useDragDrop();
  const { dropRef } = useWebDropZone({ date, mealType, onMoveMeal });

  const isDragging = dragState.isDragging;
  const isOrigin = dragState.dragOrigin?.date === date && dragState.dragOrigin?.mealType === mealType;
  const isCurrentTarget = dragState.currentDropTarget?.date === date && dragState.currentDropTarget?.mealType === mealType;
  const showDropZone = isDragging && !isOrigin;

  useEffect(() => {
    if (dropRef.current) {
      const el = dropRef.current as unknown as HTMLElement;
      const key = `${date}-${mealType}`;
      el.dataset.dropzone = JSON.stringify({ date, mealType });
      el.dataset.dropzoneKey = key;
      registerDropZone(key, { date, mealType, y: 0, height: 0, element: el });
      return () => {
        delete el.dataset.dropzone;
        delete el.dataset.dropzoneKey;
        unregisterDropZone(key);
      };
    }
  }, [date, mealType, registerDropZone, unregisterDropZone]);

  return (
    <View
      ref={dropRef}
      style={[
        styles.dropZone,
        showDropZone && styles.dropZoneActive,
        isCurrentTarget && styles.dropZoneHighlight,
      ]}
    >
      {children}
    </View>
  );
};

// Props interface
interface WeekDesktopViewProps {
  weekStartDate: string;
  meals: PlannedMeal[];
  onMealPress: (meal: PlannedMeal) => void;
  onEmptySlotPress: (date: string, mealType: MealType) => void;
  onDeleteMeal: (meal: PlannedMeal) => void;
  onSuggestAnother: (meal: PlannedMeal) => void;
  onWeekChange?: (direction: 'prev' | 'next') => void;
  onMoveMeal?: (mealId: string, newDate: string, newMealType: MealType) => void;
  onServingsChange?: (mealId: string, newServings: number) => void;
  onClearPlan?: () => void;
  hasAnyMeals?: boolean;
  onDayPress?: (date: string) => void;
  onMarkDone?: (meal: PlannedMeal) => void;
  onRateMeal?: (meal: PlannedMeal) => void;
  onGenerateWeek?: () => void;
  isGenerating?: boolean;
}

interface DayColumn {
  date: string;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  isWeekend: boolean;
  filledMeals: { meal: PlannedMeal; mealType: MealType }[];
  emptySlotTypes: MealType[];
  partialSlotTypes: MealType[];
}

export const WeekDesktopView: React.FC<WeekDesktopViewProps> = ({
  weekStartDate,
  meals,
  onMealPress,
  onEmptySlotPress,
  onDeleteMeal,
  onSuggestAnother,
  onWeekChange,
  onMoveMeal,
  onServingsChange,
  onClearPlan,
  hasAnyMeals,
  onDayPress,
  onMarkDone,
  onRateMeal,
  onGenerateWeek,
  isGenerating,
}) => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  // Live, not memoised at mount: this view outlives midnight (#845).
  const today = useToday();
  const { setScrollOffset, setScrollContainerTop } = useDragDrop();

  const handleScroll = useCallback((event: any) => {
    setScrollOffset(event.nativeEvent.contentOffset.y);
  }, [setScrollOffset]);

  const handleScrollContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const target = event.target as any;
    if (target?.measure) {
      target.measure((_x: number, _y: number, _w: number, _h: number, _px: number, pageY: number) => {
        setScrollContainerTop(pageY);
      });
    }
  }, [setScrollContainerTop]);

  // Build 7-day column structure
  const dayColumns = useMemo((): DayColumn[] => {
    const slots = generateWeekSlots(weekStartDate);
    const mealsByDateAndType = new Map<string, PlannedMeal[]>();
    meals.forEach(meal => {
      if (meal.recipe || meal.recipeId === 'free-text' || isEntryMeal(meal)) {
        const key = `${meal.scheduledFor}-${meal.mealType}`;
        const existing = mealsByDateAndType.get(key);
        if (existing) existing.push(meal);
        else mealsByDateAndType.set(key, [meal]);
      }
    });

    const uniqueDates = [...new Set(slots.map(s => s.date))];
    const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';

    return uniqueDates.map(date => {
      const dateObj = new Date(date + 'T12:00:00');
      const dayOfWeek = dateObj.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const breakfast = mealsByDateAndType.get(`${date}-breakfast`) || [];
      const lunch = mealsByDateAndType.get(`${date}-lunch`) || [];
      const dinner = mealsByDateAndType.get(`${date}-dinner`) || [];

      const filledMeals: { meal: PlannedMeal; mealType: MealType }[] = [];
      if (isWeekend) breakfast.forEach(m => filledMeals.push({ meal: m, mealType: 'breakfast' }));
      lunch.forEach(m => filledMeals.push({ meal: m, mealType: 'lunch' }));
      dinner.forEach(m => filledMeals.push({ meal: m, mealType: 'dinner' }));

      const emptySlotTypes: MealType[] = [];
      if (isWeekend && breakfast.length === 0) emptySlotTypes.push('breakfast');
      if (lunch.length === 0) emptySlotTypes.push('lunch');
      if (dinner.length === 0) emptySlotTypes.push('dinner');

      // Slots that have meals but room for more
      const partialSlotTypes: MealType[] = [];
      if (isWeekend && breakfast.length > 0 && breakfast.length < MAX_MEALS_PER_SLOT) partialSlotTypes.push('breakfast');
      if (lunch.length > 0 && lunch.length < MAX_MEALS_PER_SLOT) partialSlotTypes.push('lunch');
      if (dinner.length > 0 && dinner.length < MAX_MEALS_PER_SLOT) partialSlotTypes.push('dinner');

      return {
        date,
        dayName: dateObj.toLocaleDateString(locale, { weekday: 'long' }),
        dayNumber: dateObj.getDate(),
        isToday: date === today,
        isWeekend,
        filledMeals,
        emptySlotTypes,
        partialSlotTypes,
      };
    });
  }, [weekStartDate, meals, today, i18n.language]);

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onLayout={handleScrollContainerLayout}
      >
        {/* Masthead */}
        <DesktopMasthead
          weekStartDate={weekStartDate}
          onPreviousWeek={() => onWeekChange?.('prev')}
          onNextWeek={() => onWeekChange?.('next')}
        />

        {/* Progress Strip */}
        <View style={styles.progressRow}>
          <View style={{ flex: 1 }}>
            <WeekProgressStrip weekStartDate={weekStartDate} />
          </View>
          {hasAnyMeals && onClearPlan && (
            <TouchableOpacity style={styles.clearPlanPill} onPress={onClearPlan}>
              <Feather name="trash-2" size={13} color={Paprika.textMuted} />
            </TouchableOpacity>
          )}
        </View>

        {/* Hero Empty State or Grid */}
        {!hasAnyMeals ? (
          <View style={styles.heroEmptyState}>
            <View style={styles.heroIconContainer}>
              <MaterialCommunityIcons name="silverware-fork-knife" size={48} color={Paprika.accent} />
            </View>
            <Text style={styles.heroTitle}>{t('thisWeek.heroTitle')}</Text>
            <Text style={styles.heroSubtitle}>{t('thisWeek.heroSubtitle')}</Text>
            {onGenerateWeek && (
              <TouchableOpacity
                style={[styles.heroGenerateButton, isGenerating && styles.heroGenerateButtonDisabled]}
                onPress={onGenerateWeek}
                disabled={isGenerating}
                activeOpacity={0.8}
              >
                {isGenerating ? (
                  <>
                    <ActivityIndicator size="small" color={Paprika.background} />
                    <Text style={styles.heroGenerateButtonText}>{t('thisWeek.creatingYourPlan')}</Text>
                  </>
                ) : (
                  <>
                    <Feather name="zap" size={18} color={Paprika.background} />
                    <Text style={styles.heroGenerateButtonText}>{t('thisWeek.generateMyWeek')}</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <>
            {/* 7-Column Grid */}
            <View style={styles.columnGrid} testID="week-desktop-grid">
              {dayColumns.map(day => (
                <View key={day.date} style={styles.column}>
                  <DesktopDayHeader
                    dayName={day.dayName}
                    dayNumber={day.dayNumber}
                    isToday={day.isToday}
                  />

                  {/* Cooking plan CTA */}
                  {day.filledMeals.filter(fm => fm.mealType === 'dinner' && fm.meal.recipe).length >= 1 && (
                    <TouchableOpacity
                      style={styles.cookingPlanCTA}
                      onPress={() => router.push(`/(app)/cooking-plan/${day.date}`)}
                      activeOpacity={0.7}
                    >
                      <Feather name="clock" size={12} color={Paprika.accent} />
                      <Text style={styles.cookingPlanCTAText}>{t('thisWeek.planDay')}</Text>
                    </TouchableOpacity>
                  )}

                  {/* Filled meal cards */}
                  {day.filledMeals.map(({ meal, mealType }) => (
                    <ColumnDropZone
                      key={meal.id}
                      date={day.date}
                      mealType={mealType}
                      onMoveMeal={onMoveMeal}
                    >
                      <DesktopMealCard
                        meal={meal}
                        onPress={() => onMealPress(meal)}
                        onDelete={() => onDeleteMeal(meal)}
                        onSuggestAnother={() => onSuggestAnother(meal)}
                        onMoveMeal={onMoveMeal}
                        onMarkDone={onMarkDone}
                      />
                    </ColumnDropZone>
                  ))}

                  {/* "+ Add" for slots with room for more */}
                  {day.partialSlotTypes.map(mealType => (
                    <TouchableOpacity
                      key={`add-${mealType}`}
                      style={styles.addAnotherSlot}
                      onPress={() => onEmptySlotPress(day.date, mealType)}
                      activeOpacity={0.7}
                    >
                      <Feather name="plus" size={12} color={Paprika.accent} />
                      <Text style={styles.addAnotherSlotText}>
                        {t(`thisWeek.mealTypes.${mealType}`)}
                      </Text>
                    </TouchableOpacity>
                  ))}

                  {/* Empty slot placeholders */}
                  {day.emptySlotTypes.map(mealType => (
                    <ColumnDropZone
                      key={`empty-${mealType}`}
                      date={day.date}
                      mealType={mealType}
                      onMoveMeal={onMoveMeal}
                    >
                      <TouchableOpacity
                        style={styles.emptySlot}
                        onPress={() => onEmptySlotPress(day.date, mealType)}
                        testID={`legacy-empty-${day.date}-${mealType}`}
                        activeOpacity={0.7}
                      >
                        <Feather name="plus" size={16} color={Paprika.textMuted} />
                        <Text style={styles.emptySlotText}>
                          {t(`thisWeek.mealTypes.${mealType}`)}
                        </Text>
                      </TouchableOpacity>
                    </ColumnDropZone>
                  ))}

                </View>
              ))}
            </View>

          </>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 80,
  },

  // Progress strip row
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: PaprikaSpacing.lg,
  },
  clearPlanPill: {
    backgroundColor: Paprika.surfaceLight,
    width: 32,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: PaprikaRadius.xl,
  },

  // 7-Column Grid (CSS Grid on web)
  columnGrid: {
    display: 'flex' as any,
    flexDirection: 'row',
    gap: PaprikaSpacing.md,
    padding: PaprikaSpacing.lg,
    flex: 1,
    // CSS Grid override for web
    ...(({
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
    }) as any),
  },
  column: {
    flex: 1,
    minWidth: 0,
  },

  // Empty slot
  emptySlot: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    paddingVertical: PaprikaSpacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginBottom: PaprikaSpacing.sm,
    backgroundColor: Paprika.surfaceLow,
  },
  emptySlotText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontWeight: '400',
  },
  addAnotherSlot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: PaprikaSpacing.xs,
    marginBottom: PaprikaSpacing.xs,
  },
  addAnotherSlotText: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
    fontWeight: '500',
  },
  cookingPlanCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: PaprikaSpacing.xs,
    marginTop: PaprikaSpacing.xs,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
  },
  cookingPlanCTAText: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
    fontWeight: '500',
  },

  // Drop zones
  dropZone: {
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  dropZoneActive: {
    borderStyle: 'dashed',
    borderColor: Paprika.accent,
  },
  dropZoneHighlight: {
    backgroundColor: 'rgba(193, 127, 89, 0.15)',
    borderStyle: 'solid',
  },

  // Hero Empty State
  heroEmptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.xl,
    paddingVertical: PaprikaSpacing.xxl,
  },
  heroIconContainer: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: Paprika.accentLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: PaprikaSpacing.lg,
  },
  heroTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.sm,
  },
  heroSubtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 400,
    marginBottom: PaprikaSpacing.lg,
  },
  heroGenerateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.accent,
    paddingVertical: 14,
    paddingHorizontal: PaprikaSpacing.xl,
    borderRadius: PaprikaRadius.xl,
  },
  heroGenerateButtonDisabled: {
    opacity: 0.7,
  },
  heroGenerateButtonText: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.background,
    fontWeight: '600',
  },
});

export default WeekDesktopView;
